import {
  ChartLineUpIcon,
  BellIcon,
  BuildingsIcon,
  CaretDownIcon,
  CaretUpDownIcon,
  EyeIcon,
  GaugeIcon,
  GlobeSimpleIcon,
  MoonIcon,
  MonitorIcon,
  PlusIcon,
  PlugsConnectedIcon,
  ShieldCheckIcon,
  SignOutIcon,
  SunIcon,
  UserCircleIcon,
  WalletIcon,
} from "@phosphor-icons/react";
import { Button } from "@cloudflare/kumo/components/button";
import { DropdownMenu } from "@cloudflare/kumo/components/dropdown";
import { Sidebar } from "@cloudflare/kumo/components/sidebar";
import { useEffect, useState, type ReactNode } from "react";
import { useRouter } from "../router";
import { api } from "../api";
import { useAuth } from "../auth";
import { demoMode } from "../mode";
import { type ThemePreference, useThemePreference } from "../theme";
import { useSite } from "../site";

const themeOptions = [
  { value: "system", label: "System", icon: MonitorIcon },
  { value: "light", label: "Light", icon: SunIcon },
  { value: "dark", label: "Dark", icon: MoonIcon },
] as const;

function ThemeMenu({
  mobile = false,
  preference,
  onPreferenceChange,
}: {
  mobile?: boolean;
  preference: ThemePreference;
  onPreferenceChange: (preference: ThemePreference) => void;
}) {
  const selected = themeOptions.find((option) => option.value === preference) ?? themeOptions[0];

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger
        render={
          <Button
            className={mobile ? undefined : "kumo-theme-trigger"}
            shape="square"
            size={mobile ? "sm" : "xs"}
            variant="ghost"
            icon={selected.icon}
            aria-label={`Color theme: ${selected.label}`}
          />
        }
      />
      <DropdownMenu.Content align={mobile ? "end" : "start"}>
        <DropdownMenu.Group>
          <DropdownMenu.Label>Color theme</DropdownMenu.Label>
          <DropdownMenu.RadioGroup
            value={preference}
            onValueChange={(value) => onPreferenceChange(value as ThemePreference)}
          >
            {themeOptions.map(({ value, label, icon }) => (
              <DropdownMenu.RadioItem key={value} value={value} icon={icon}>
                {label}
                <DropdownMenu.RadioItemIndicator />
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Group>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}

const navigation = [
  { to: "/", label: "Overview", icon: GaugeIcon },
  { to: "/click-events", label: "Click events", icon: ChartLineUpIcon },
  { to: "/channels", label: "Channels", icon: PlugsConnectedIcon },
  { to: "/protection-rules", label: "Detection rules", icon: ShieldCheckIcon },
  { to: "/rules", label: "Alerts", icon: BellIcon },
] as const;

function formatCompactCredits(balance: number) {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(balance);
}

function ConsoleNavigation({
  themePreference,
  onThemePreferenceChange,
}: {
  themePreference: ThemePreference;
  onThemePreferenceChange: (preference: ThemePreference) => void;
}) {
  const { path, search } = useRouter();
  const { user, signOut } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const { sites, activeSite, selectSite } = useSite();
  const activeSiteLabel = activeSite.name || new URL(activeSite.allowed_origins[0]).hostname;
  const accountName = user?.displayName?.trim() || user?.email?.split("@")[0] || "Account";
  const accountInitials = accountName
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  useEffect(() => {
    if (demoMode) return;
    void api.adminAccess().then((result) => setIsAdmin(result.is_admin)).catch(() => setIsAdmin(false));
  }, []);

  useEffect(() => {
    if (demoMode) return;
    let cancelled = false;
    const refreshBalance = () => {
      void api.billing().then(({ account }) => {
        if (!cancelled) setCreditBalance(account.balance);
      }).catch(() => {
        if (!cancelled) setCreditBalance(null);
      });
    };
    refreshBalance();
    window.addEventListener("focus", refreshBalance);
    window.addEventListener("juicytraffic:billing-updated", refreshBalance);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", refreshBalance);
      window.removeEventListener("juicytraffic:billing-updated", refreshBalance);
    };
  }, [path, search]);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <Sidebar className="juicy-kumo-sidebar">
      <Sidebar.Header>
        <div className="kumo-sidebar-heading">
          <a className="kumo-brand" href="/" aria-label="JuicyTraffic overview">
            <span className="kumo-brand-mark"><img src="/juicytraffic-logo.svg" alt="" /></span>
            <span><strong>JuicyTraffic</strong></span>
          </a>
          <Sidebar.Trigger />
        </div>
      </Sidebar.Header>

      <Sidebar.Content>
        <Sidebar.Group className="site-switcher-group">
          <Sidebar.GroupLabel>Current site</Sidebar.GroupLabel>
          <DropdownMenu>
            <DropdownMenu.Trigger render={
              <button className="site-switcher" type="button" aria-label={`Current site: ${activeSiteLabel}`}>
                <GlobeSimpleIcon className="site-switcher__icon" size={17} aria-hidden="true" />
                <span className="site-switcher__label">{activeSiteLabel}</span>
                <CaretDownIcon className="site-switcher__chevron" size={14} aria-hidden="true" />
              </button>
            } />
            <DropdownMenu.Content align="start" className="site-switcher-menu">
              <DropdownMenu.Group>
                <DropdownMenu.Label>Switch site</DropdownMenu.Label>
                <DropdownMenu.RadioGroup value={activeSite.site_id} onValueChange={selectSite}>
                  {sites.map((site) => {
                    const siteLabel = site.name || new URL(site.allowed_origins[0]).hostname;
                    return (
                      <DropdownMenu.RadioItem key={site.site_id} value={site.site_id} icon={GlobeSimpleIcon}>
                        <span className="site-switcher-menu__site">{siteLabel}</span>
                        <DropdownMenu.RadioItemIndicator />
                      </DropdownMenu.RadioItem>
                    );
                  })}
                </DropdownMenu.RadioGroup>
              </DropdownMenu.Group>
              {!demoMode && <>
                <DropdownMenu.Separator />
                <DropdownMenu.LinkItem href="/sites" icon={PlusIcon}>Manage sites</DropdownMenu.LinkItem>
              </>}
            </DropdownMenu.Content>
          </DropdownMenu>
        </Sidebar.Group>

        <Sidebar.Group className="kumo-primary-navigation">
          <Sidebar.Menu>
            {navigation.map(({ to, label, icon }) => {
              const unavailableInDemo = demoMode && to === "/rules";
              return (
                <Sidebar.MenuButton
                  key={to}
                  icon={icon}
                  href={unavailableInDemo ? undefined : to}
                  active={!unavailableInDemo && path === to}
                  disabled={unavailableInDemo}
                  tooltip={label}
                  aria-current={!unavailableInDemo && path === to ? "page" : undefined}
                >
                  {label}
                </Sidebar.MenuButton>
              );
            })}
          </Sidebar.Menu>
        </Sidebar.Group>
      </Sidebar.Content>

      <Sidebar.Footer className={!demoMode ? "kumo-account-footer h-auto flex-col items-stretch gap-1 py-2" : undefined}>
        {!demoMode && <Sidebar.Menu>
          <Sidebar.MenuItem>
            <Sidebar.MenuButton
              icon={WalletIcon}
              href="/usage"
              active={path === "/usage"}
              tooltip={`Click credits · ${creditBalance === null ? "Balance unavailable" : new Intl.NumberFormat("en-US").format(creditBalance)} · Credits & billing`}
              aria-current={path === "/usage" ? "page" : undefined}
              aria-label={`Click credits: ${creditBalance === null ? "balance unavailable" : new Intl.NumberFormat("en-US").format(creditBalance)}. Open credits and billing`}
            >
              Click credits
              <Sidebar.MenuBadge className="kumo-credit-balance" title={creditBalance === null ? "Balance unavailable" : `${new Intl.NumberFormat("en-US").format(creditBalance)} click credits`}>
                {creditBalance === null ? "—" : formatCompactCredits(creditBalance)}
              </Sidebar.MenuBadge>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>
        </Sidebar.Menu>}
        {!demoMode ? <DropdownMenu>
          <DropdownMenu.Trigger render={
            <Sidebar.MenuButton
              className="kumo-account-trigger"
              icon={<span className="kumo-account-avatar" aria-hidden="true">{accountInitials}</span>}
              tooltip={`${accountName} · ${user?.email || "Account"}`}
            >
              <span className="kumo-account-identity"><strong>{accountName}</strong><small>{user?.email || ""}</small></span>
              <CaretUpDownIcon className="kumo-account-chevron" size={14} aria-hidden="true" />
            </Sidebar.MenuButton>
          } />
          <DropdownMenu.Content align="start" side="top" className="kumo-account-menu">
            <DropdownMenu.Group>
              <DropdownMenu.Label>Workspace</DropdownMenu.Label>
              <DropdownMenu.LinkItem href="/sites" icon={BuildingsIcon}>Sites</DropdownMenu.LinkItem>
              <DropdownMenu.LinkItem href="/usage" icon={WalletIcon}>
                Credits &amp; billing
              </DropdownMenu.LinkItem>
            </DropdownMenu.Group>
            <DropdownMenu.Separator />
            <DropdownMenu.Group>
              <DropdownMenu.LinkItem href="/profile" icon={UserCircleIcon}>Profile</DropdownMenu.LinkItem>
              {isAdmin && <DropdownMenu.LinkItem href="/admin" icon={ShieldCheckIcon}>Admin</DropdownMenu.LinkItem>}
            </DropdownMenu.Group>
            <DropdownMenu.Separator />
            <DropdownMenu.Group>
              <DropdownMenu.Label>Theme</DropdownMenu.Label>
              <DropdownMenu.RadioGroup value={themePreference} onValueChange={(value) => onThemePreferenceChange(value as ThemePreference)}>
                {themeOptions.map(({ value, label, icon }) => <DropdownMenu.RadioItem key={value} value={value} icon={icon}>{label}<DropdownMenu.RadioItemIndicator /></DropdownMenu.RadioItem>)}
              </DropdownMenu.RadioGroup>
            </DropdownMenu.Group>
            <DropdownMenu.Separator />
            <DropdownMenu.Item icon={SignOutIcon} variant="danger" disabled={signingOut} onClick={() => void handleSignOut()}>{signingOut ? "Signing out…" : "Sign out"}</DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu> : <>
          <a className="kumo-demo-cta" href="https://app.juicytraffic.com/">Create account</a>
          <ThemeMenu preference={themePreference} onPreferenceChange={onThemePreferenceChange} />
        </>}
      </Sidebar.Footer>
      <Sidebar.Rail />
    </Sidebar>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const { search, navigate } = useRouter();
  const [themePreference, setThemePreference] = useThemePreference();
  const viewParams = new URLSearchParams(search);
  const viewedTenant = viewParams.get("admin_tenant")?.trim();
  const viewedUser = viewParams.get("admin_user")?.trim() || viewedTenant;

  return (
    <Sidebar.Provider defaultOpen collapsible="icon" mobileBreakpoint={800} className="kumo-console-shell">
      <ConsoleNavigation themePreference={themePreference} onThemePreferenceChange={setThemePreference} />
      <section className="kumo-content-shell">
        <header className="kumo-mobile-bar">
          <Sidebar.Trigger />
          <span className="kumo-mobile-brand"><img src="/juicytraffic-logo.svg" alt="" />JuicyTraffic</span>
          <span className="kumo-mobile-actions">
            <ThemeMenu mobile preference={themePreference} onPreferenceChange={setThemePreference} />
            {!demoMode && <Button shape="square" variant="ghost" icon={UserCircleIcon} aria-label="Profile" onClick={() => navigate("/profile")} />}
          </span>
        </header>
        <main className="main-content">
          {demoMode && <div className="demo-banner" role="status">
            <span><strong>Demo account</strong><small>Sample data · changes reset when you leave</small></span>
            <a href="https://app.juicytraffic.com/">Create your account</a>
          </div>}
          {viewedTenant && <div className="admin-viewing-banner" role="status">
            <EyeIcon size={16} aria-hidden="true" />
            <span>Viewing user <strong>{viewedUser}</strong></span>
            <small>Read-only support view</small>
            <Button size="xs" variant="secondary" icon={SignOutIcon} onClick={() => navigate("/admin")}>Exit view</Button>
          </div>}
          {children}
        </main>
      </section>
    </Sidebar.Provider>
  );
}
